import { ObjectId } from "bson";
import * as z from "zod/v4";

export const CustomAnalyticsReportColumnKindSchema = z.enum([
  "text",
  "number",
  "integer",
  "currency",
  "percent",
  "date",
  "datetime",
  "boolean",
  "json",
]);

export type CustomAnalyticsReportColumnKind = z.infer<
  typeof CustomAnalyticsReportColumnKindSchema
>;

export const CustomAnalyticsReportColumnSchema = z.object({
  key: z.string(),
  sourceName: z.string(),
  label: z.string(),
  sqlType: z.string(),
  kind: CustomAnalyticsReportColumnKindSchema,
  order: z.number().int().nonnegative(),
  align: z.enum(["left", "right"]).default("left"),
});

export type CustomAnalyticsReportColumn = z.infer<
  typeof CustomAnalyticsReportColumnSchema
>;

export const CustomAnalyticsReportColumnsSchema = z.array(
  CustomAnalyticsReportColumnSchema,
);

export interface CustomAnalyticsReportSourceColumn {
  name: string;
  type: string;
  scale?: number | null;
  precision?: number | null;
}

const REDO_RETURNS_REFERENCE = /\bREDO\s*\.\s*RETURNS\b/i;
const FORBIDDEN_STATEMENT_KEYWORDS = [
  "DROP",
  "CREATE",
  "DELETE",
  "INSERT",
  "UPDATE",
  "TRUNCATE",
  "ALTER",
  "GRANT",
  "REVOKE",
  "MERGE",
  "COPY",
  "CALL",
  "EXECUTE",
  "USE",
];
const FORBIDDEN_STATEMENT_PATTERN = new RegExp(
  `\\b(${FORBIDDEN_STATEMENT_KEYWORDS.join("|")})\\b`,
  "i",
);
const SNOWFLAKE_TIMESTAMP_TZ_OFFSET_SEPARATOR =
  /(\d{2}:\d{2}:\d{2}(?:\.\d+)?) ([+-]\d{4})$/;
const CAMEL_CASE_WORD_BOUNDARY = /([a-z0-9])([A-Z])/g;
const COLUMN_WORD_SEPARATOR = /[^a-zA-Z0-9]+/;

export function stripTrailingSqlSemicolon(sql: string): string {
  return sql.trim().replace(/;\s*$/, "");
}

export function assertCustomAnalyticsReportSqlIsSafe(sql: string): void {
  const withoutStrings = sql.replace(/'[^']*'/g, "''");
  const normalized = withoutStrings.replace(/["`[\]]/g, "");
  if (!/^\s*(WITH|SELECT)\b/i.test(normalized)) {
    throw new Error("Custom analytics report SQL must be a SELECT query.");
  }
  if (normalized.includes(";")) {
    throw new Error(
      "Custom analytics report SQL must contain a single SELECT statement.",
    );
  }
  if (REDO_RETURNS_REFERENCE.test(normalized)) {
    throw new Error(
      "Custom analytics report SQL must only reference scoped report tables.",
    );
  }
  const forbiddenMatch = FORBIDDEN_STATEMENT_PATTERN.exec(normalized);
  if (forbiddenMatch) {
    throw new Error(
      `Custom analytics report SQL contains forbidden keyword "${forbiddenMatch[1]}".`,
    );
  }
}

export function wrapCustomAnalyticsReportSqlWithTeamScope(
  sql: string,
  teamId: ObjectId,
): string {
  const teamIdHex = teamId.toString();
  if (!ObjectId.isValid(teamIdHex)) {
    throw new Error(
      `Invalid team id for custom analytics report: ${teamIdHex}`,
    );
  }

  const strippedSql = stripTrailingSqlSemicolon(sql);
  assertCustomAnalyticsReportSqlIsSafe(strippedSql);

  const scopedCtes = `scoped_opr AS (
  SELECT * FROM REDO.RETURNS.ORDER_PRODUCT_REPORTS
  WHERE team_id = '${teamIdHex}'
),
scoped_rpr AS (
  SELECT * FROM REDO.RETURNS.RETURN_PRODUCT_REPORTS
  WHERE team_id = '${teamIdHex}'
),
scoped_rr AS (
  SELECT * FROM REDO.RETURNS.RETURN_REPORTS
  WHERE team = '${teamIdHex}'
)`;
  const trimmed = strippedSql.trimStart();
  const leadingWith = /^WITH\s+/i;
  if (leadingWith.test(trimmed)) {
    const afterWith = trimmed.replace(leadingWith, "");
    return `WITH ${scopedCtes},\n${afterWith}`;
  }
  return `WITH ${scopedCtes}\n${strippedSql}`;
}

export function buildCustomAnalyticsReportMetadataSql(
  sql: string,
  teamId: ObjectId,
): string {
  const scopedSql = wrapCustomAnalyticsReportSqlWithTeamScope(sql, teamId);
  return `SELECT * FROM (\n${scopedSql}\n) AS custom_report\nLIMIT 0`;
}

export function buildCustomAnalyticsReportRowsSql({
  sql,
  teamId,
  limit,
  offset,
}: {
  sql: string;
  teamId: ObjectId;
  limit: number;
  offset: number;
}): string {
  const clampedLimit = Math.max(1, Math.min(Math.trunc(limit), 100));
  const clampedOffset = Math.max(0, Math.trunc(offset));
  const scopedSql = wrapCustomAnalyticsReportSqlWithTeamScope(sql, teamId);
  return `SELECT * FROM (\n${scopedSql}\n) AS custom_report\nLIMIT ${clampedLimit}\nOFFSET ${clampedOffset}`;
}

export function buildCustomAnalyticsReportStreamSql(
  sql: string,
  teamId: ObjectId,
): string {
  return wrapCustomAnalyticsReportSqlWithTeamScope(sql, teamId);
}

export function normalizeCustomAnalyticsReportTimestampValue(
  dateString: string,
): string {
  /*
   * Snowflake TIMESTAMP_TZ values can arrive from the JS driver like:
   *   "2025-10-08 07:52:25.364 -0600"
   *
   * Safari treats the space before the numeric timezone offset as invalid when
   * passed to `new Date(...)`; Chrome is more permissive. Removing only that
   * final separator gives:
   *   "2025-10-08 07:52:25.364-0600"
   *
   * The regex is anchored to the end of the string and requires a time plus
   * numeric timezone offset so we do not change ordinary spaces in the date
   * portion of the timestamp.
   */
  return dateString.replace(SNOWFLAKE_TIMESTAMP_TZ_OFFSET_SEPARATOR, "$1$2");
}

export function buildCustomAnalyticsReportColumns(
  sourceColumns: CustomAnalyticsReportSourceColumn[],
): CustomAnalyticsReportColumn[] {
  const usedKeys = new Map<string, number>();
  const seenSourceNames = new Set<string>();

  return sourceColumns.map((sourceColumn, index) => {
    const sourceNameKey = sourceColumn.name.toUpperCase();
    if (seenSourceNames.has(sourceNameKey)) {
      throw new Error(
        `Custom analytics report has duplicate column name "${sourceColumn.name}".`,
      );
    }
    seenSourceNames.add(sourceNameKey);

    const keyBase = normalizeCustomAnalyticsReportColumnKey(sourceColumn.name);
    const keyCount = usedKeys.get(keyBase) ?? 0;
    usedKeys.set(keyBase, keyCount + 1);
    const key = keyCount === 0 ? keyBase : `${keyBase}${keyCount + 1}`;
    const kind = inferCustomAnalyticsReportColumnKind(sourceColumn);

    return {
      key,
      sourceName: sourceColumn.name,
      label: labelFromCustomAnalyticsReportColumnName(sourceColumn.name),
      sqlType: sourceColumn.type,
      kind,
      order: index,
      align: "left",
    };
  });
}

function normalizeCustomAnalyticsReportColumnKey(sourceName: string): string {
  const words = splitCustomAnalyticsReportColumnName(sourceName);
  if (words.length === 0) {
    return "column";
  }

  const firstWord = words[0]!;
  const restWords = words.slice(1);
  const normalized =
    firstWord.toLowerCase() +
    restWords.map((word) => capitalizeColumnWord(word)).join("");

  return startsWithAsciiLetter(normalized) ? normalized : `column${normalized}`;
}

function labelFromCustomAnalyticsReportColumnName(sourceName: string): string {
  const words = splitCustomAnalyticsReportColumnName(sourceName);
  if (words.length === 0) {
    return "Column";
  }

  return words
    .map((word) => {
      const lowerWord = word.toLowerCase();
      return isKnownColumnAcronym(lowerWord)
        ? lowerWord.toUpperCase()
        : capitalizeColumnWord(lowerWord);
    })
    .join(" ");
}

function splitCustomAnalyticsReportColumnName(sourceName: string): string[] {
  /*
   * Snowflake usually gives us SCREAMING_SNAKE_CASE column names, while quoted
   * SQL aliases can be camelCase, spaced, or punctuation-separated. Normalize
   * all of those into plain alphanumeric words so the internal row key and
   * visible table label are derived from the same source.
   */
  return sourceName
    .trim()
    .replace(CAMEL_CASE_WORD_BOUNDARY, "$1 $2")
    .split(COLUMN_WORD_SEPARATOR)
    .filter(Boolean);
}

function capitalizeColumnWord(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function startsWithAsciiLetter(value: string): boolean {
  const firstCharCode = value.charCodeAt(0);
  return (
    (firstCharCode >= 65 && firstCharCode <= 90) ||
    (firstCharCode >= 97 && firstCharCode <= 122)
  );
}

function isKnownColumnAcronym(word: string): boolean {
  return ["ai", "id", "sku", "upc", "rma", "url"].includes(word);
}

function inferCustomAnalyticsReportColumnKind(
  sourceColumn: CustomAnalyticsReportSourceColumn,
): CustomAnalyticsReportColumnKind {
  const type = sourceColumn.type.toUpperCase();
  const name = sourceColumn.name.toLowerCase();

  if (type.includes("TIMESTAMP")) {
    return "datetime";
  }
  if (type === "DATE" || name.endsWith("_date") || name.endsWith("_day")) {
    return "date";
  }
  if (type === "BOOLEAN") {
    return "boolean";
  }
  if (
    type === "VARIANT" ||
    type === "OBJECT" ||
    type === "ARRAY" ||
    type === "MAP"
  ) {
    return "json";
  }
  if (name.endsWith("_at")) {
    return "datetime";
  }
  if (name.endsWith("_month")) {
    return "date";
  }
  if (isNumericSnowflakeType(type)) {
    if (isPercentColumnName(name)) {
      return "percent";
    }
    if (isCurrencyColumnName(name)) {
      return "currency";
    }
    if (isIntegerColumnName(name) || sourceColumn.scale === 0) {
      return "integer";
    }
    return "number";
  }

  return "text";
}

function isNumericSnowflakeType(type: string): boolean {
  return (
    type === "NUMBER" ||
    type === "DECIMAL" ||
    type === "NUMERIC" ||
    type === "INT" ||
    type === "INTEGER" ||
    type === "BIGINT" ||
    type === "FLOAT" ||
    type === "DOUBLE" ||
    type === "REAL"
  );
}

function isCurrencyColumnName(name: string): boolean {
  return /\b(amount|price|refund|revenue|cost|tax|subtotal|total|value|sales|gmv)\b/.test(
    name.replace(/_/g, " "),
  );
}

function isPercentColumnName(name: string): boolean {
  return /\b(rate|percent|percentage|pct|ratio)\b/.test(
    name.replace(/_/g, " "),
  );
}

function isIntegerColumnName(name: string): boolean {
  return /\b(count|quantity|qty|number|orders|returns|items|units)\b/.test(
    name.replace(/_/g, " "),
  );
}
